import React from 'react';
import {useTranslation} from 'react-i18next';

import {AppSwitcherProps, LangCodes, LangCodesType, AppSwitcher as TechpulseAppSwitcher} from '../WFCAppSwitcher';

import useStyles from './styles';

const AppSwitcher = ({
    loginType,
    disabled,
    trailing,
    breadcrumbs,
    homeUrl = '/',
    environment,
    isHPEmail,
}: {
    environment: AppSwitcherProps['environment'];
    homeUrl?: AppSwitcherProps['currentAppHomeUrl'];
    breadcrumbs: AppSwitcherProps['breadcrumbs'];
    loginType: AppSwitcherProps['loginType'];
    isHPEmail: AppSwitcherProps['isHpEmail'];
    disabled: AppSwitcherProps['disableAppSwitcher'];
    trailing?: React.ReactNode;
}) => {
    const {t, i18n} = useTranslation(['AppSwitcher']);

    const styles = useStyles();

    const supportedLanguages = Object.values(LangCodes);

    // For languages that have a different casing / format to our definitons.
    const langcodeMappings: Record<string, LangCodesType> = {
        'pt-BR': LangCodes.pt_BR,
    };

    const currentLanguage: LangCodesType = langcodeMappings?.[i18n.language] || (i18n.language as LangCodesType);

    const appSwitcherConfig: Partial<AppSwitcherProps> = {
        currentAppHomeUrl: homeUrl,
        // Check whether the app switcher supports the language and use english if not.
        currentLanguage: /* istanbul ignore next */ supportedLanguages.includes(currentLanguage)
            ? currentLanguage
            : LangCodes.en,
        breadcrumbs: breadcrumbs,
        disableAppSwitcher: disabled,
        environment: environment,
        currentAppId: 'hp_wolf_security',
        loginType: loginType,
        isHpEmail: isHPEmail,
        i18n: {
            appSwicherMenu: t('AppSwitcher:appSwitcher', 'App switcher'),
            breadcrumbs: t('AppSwitcher:Breadcrumbs', 'Breadcrumbs'),
            home: t('AppSwitcher:Home', 'Home'),
            openAppSwitcher: t('AppSwitcher:openAppSwitcher', 'Open app switcher'),
            closeAppSwitcher: t('AppSwitcher:closeAppSwitcher', 'Close app switcher'),
            myCompany: t('AppSwitcher:myCompany', 'My Company'),
            settings: t('AppSwitcher:settings', 'Settings'),
            tools: t('AppSwitcher:tools', 'Tools'),
            services: t('AppSwitcher:services', 'Services'),
            expandServices: t('AppSwitcher:toggleServicesMenu', 'Toggle services menu'),
            expandTools: t('AppSwitcher:toggleToolsMenu', 'Toggle tools menu'),
            usersAndRoles: t('AppSwitcher:usersAndRoles', 'Users & Roles'),
            helpAndSupport: t('AppSwitcher:helpAndSupport', 'Help & Support'),
            ariaLabels: {
                tools: {
                    aria: t('AppSwitcher:toggleToolsMenu', 'Toggle tools menu'),
                    toggle: t('AppSwitcher:tools', 'Tools'),
                },
                categories: {
                    aria: t('AppSwitcher:toggleServicesMenu', 'Toggle services menu'),
                    toggle: t('AppSwitcher:services', 'Services'),
                },
            },
        },
    };

    return (
        <div
            data-testid="AppSwitcher"
            className={styles.wrapper}
        >
            <TechpulseAppSwitcher
                trailing={trailing}
                config={appSwitcherConfig}
            />
        </div>
    );
};

export default AppSwitcher;
